import { useCallback, useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import { boxInput, currentMonth } from "../lib/format";
import { AppHeader } from "../components/site/AppHeader";
import { EmployeeForm } from "../components/admin/EmployeeForm";
import { PayslipForm } from "../components/admin/PayslipForm";
import { PayslipList } from "../components/payslip/PayslipList";
import { AttendanceSummary, AttendanceTable } from "../components/attendance/AttendanceTable";

const Section = ({ title, children, testId }) => (
  <section className="mt-14" data-testid={testId}>
    <h2 className="mb-5 font-display text-4xl font-black uppercase">{title}</h2>
    {children}
  </section>
);

export default function AdminEmployee() {
  const { id } = useParams();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [emp, setEmp] = useState(null);
  const [month, setMonth] = useState(currentMonth());
  const [att, setAtt] = useState([]);
  const [slips, setSlips] = useState([]);

  const loadEmp = useCallback(() => api.get(`/admin/employees/${id}`).then((r) => setEmp(r.data)).catch((e) => toast.error(formatApiError(e))), [id]);
  const loadAtt = useCallback(() => api.get(`/admin/employees/${id}/attendance`, { params: { month } }).then((r) => setAtt(Array.isArray(r.data) ? r.data : [])).catch(() => setAtt([])), [id, month]);
  const loadSlips = useCallback(() => api.get(`/admin/employees/${id}/payslips`).then((r) => setSlips(Array.isArray(r.data) ? r.data : [])).catch(() => setSlips([])), [id]);

  useEffect(() => { if (user?.role === "admin") { loadEmp(); loadSlips(); } return undefined; }, [user, loadEmp, loadSlips]);
  useEffect(() => { if (user?.role === "admin") loadAtt(); return undefined; }, [user, loadAtt]);

  if (user === null) return <div className="min-h-screen bg-[var(--ink)]" />;
  if (!user || user.role !== "admin") return <Navigate to="/admin/login" replace />;

  const update = async (body) => {
    try { const { data } = await api.patch(`/admin/employees/${id}`, body); setEmp(data); toast.success("Employee updated"); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const saveDay = async (date, body) => {
    try { await api.put(`/admin/employees/${id}/attendance/${date}`, body); toast.success(`Saved ${date}`); loadAtt(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const clearDay = async (date) => { await api.delete(`/admin/employees/${id}/attendance/${date}`); loadAtt(); };
  const delSlip = async (s) => {
    if (!window.confirm("Delete this salary slip?")) return;
    try { await api.delete(`/admin/payslips/${s.id}`); loadSlips(); } catch (err) { toast.error(formatApiError(err)); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete ${emp.name} with all attendance and salary slips?`)) return;
    try { await api.delete(`/admin/employees/${id}`); toast.success("Employee deleted"); nav("/admin?tab=employees"); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <main className="min-h-screen bg-[var(--ink)] pb-24" data-testid="admin-employee-page">
      <AppHeader onLogout={logout} home="/admin" />
      <div className="mx-auto max-w-[1280px] px-5 py-10 md:px-10">
        <Link to="/admin?tab=employees" className="flex items-center gap-2 text-sm text-[var(--bone-2)] hover:text-[var(--signal)]" data-testid="back-to-employees"><ArrowLeft className="h-4 w-4" /> All employees</Link>
        {emp && (
          <>
            <div className="mt-6 flex flex-wrap items-end justify-between gap-4">
              <div>
                <p className="font-mono text-[var(--signal)]" data-testid="employee-code">{emp.emp_code}</p>
                <h1 className="font-display text-5xl font-black uppercase md:text-7xl" data-testid="employee-name">{emp.name}</h1>
              </div>
              <div className="flex gap-2">
                <button onClick={() => update({ active: !emp.active })} data-testid="employee-toggle-active"
                  className="rounded-full border border-white/25 px-4 py-2 text-sm hover:border-[var(--signal)]">{emp.active ? "Deactivate login" : "Activate login"}</button>
                <button onClick={remove} className="rounded-full border border-red-400/50 px-4 py-2 text-sm text-red-300" data-testid="employee-delete">Delete</button>
              </div>
            </div>
            <Section title="Profile" testId="employee-profile-section">
              <EmployeeForm key={emp.id + emp.name} initial={{ ...emp, password: "" }} onSubmit={update} submitLabel="Save changes" />
            </Section>
          </>
        )}
        <Section title="Attendance" testId="employee-attendance-section">
          <input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} className={`${boxInput} mb-5 w-auto`} data-testid="attendance-month-input" />
          <AttendanceSummary records={att} />
          <div className="mt-6"><AttendanceTable month={month} records={att} editable onSave={saveDay} onClear={clearDay} /></div>
        </Section>
        <Section title="Salary slips" testId="employee-payslips-section">
          <PayslipForm employeeId={id} onSaved={loadSlips} />
          <div className="mt-8"><PayslipList slips={slips} onDelete={delSlip} /></div>
        </Section>
      </div>
    </main>
  );
}
